import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Search, Package, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Order lookup (2026-09-11): customers enter the email used at checkout plus the
// order number from their confirmation email. The old email-only search let
// anyone see another customer's orders.
const OrderHistoryPage = () => {
  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim() || !orderNumber.trim()) {
      toast.error('Enter your email address and order number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders/lookup`, {
        email: email.trim(),
        order_number: orderNumber.trim()
      });
      setOrder(response.data);
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 422) {
        setOrder(null);
      } else {
        console.error('Error looking up order:', error);
        toast.error('Could not look up your order. Please try again.');
        setLoading(false);
        return;
      }
    }
    setSearched(true);
    setLoading(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', icon: Clock, label: 'Pending' },
      paid: { variant: 'default', icon: CheckCircle, label: 'Paid', className: 'bg-green-100 text-green-800' },
      completed: { variant: 'default', icon: CheckCircle, label: 'Completed', className: 'bg-green-100 text-green-800' },
      cancelled: { variant: 'destructive', icon: XCircle, label: 'Cancelled' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Payment failed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-history-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-navy-700">Home</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="text-slate-900 font-medium">Order History</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            Order History
          </h1>
          <p className="text-slate-600 mb-8">
            Enter the email address you used at checkout and the order number from your confirmation email.
          </p>

          {/* Lookup Form */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="email" className="sr-only">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    autoComplete="email"
                    data-testid="order-email-input"
                  />
                </div>
                <div className="md:w-48">
                  <Label htmlFor="order_number" className="sr-only">Order Number</Label>
                  <Input
                    id="order_number"
                    type="text"
                    placeholder="Order number"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    className="h-11"
                    autoComplete="off"
                    data-testid="order-number-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-navy-900 hover:bg-navy-800 h-11 px-6"
                  disabled={loading}
                  data-testid="search-orders-btn"
                >
                  {loading ? (
                    <div className="spinner h-4 w-4 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Find order
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Result */}
          {searched && (
            <>
              {!order ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                      No Order Found
                    </h3>
                    <p className="text-slate-600 mb-6">
                      No order matches that email and order number. The order number is in your confirmation email and starts with #.
                    </p>
                    <Link to="/products">
                      <Button className="bg-navy-900 hover:bg-navy-800">
                        Start Shopping
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="orders-list">
                  <Card key={order.id} data-testid={`order-${order.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg">
                            Order #{order.id.slice(0, 8)}
                          </CardTitle>
                          <p className="text-sm text-slate-500 mt-1">
                            {formatDate(order.created_at)}
                          </p>
                        </div>
                        {getStatusBadge(order.status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              {item.product_name} x {item.quantity}
                            </span>
                            <span className="text-slate-900">
                              {formatPrice(item.price * item.quantity)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t pt-3 flex justify-between items-center">
                        <span className="font-medium text-slate-900">Total</span>
                        <span className="font-heading text-xl font-bold text-navy-900">
                          {formatPrice(order.total_amount)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryPage;
